import css from './entityHistory.module.scss';

import cx from 'classnames';
import dayjs from 'dayjs';
import { Fragment, memo, useEffect, useState } from 'react';

import { apiGetQuestion, apiGetWord } from 'core/api';
import { getRelativeTime } from 'core/helpers/dates';
import { EXERCISE_LABELS } from 'core/helpers/deck';
import { getExercisesStatuses } from 'core/helpers/exercise';
import Button from 'core/ui/Button';
import { useMessageModal } from 'core/ui/MessageModal';
import Preloader from 'core/ui/Preloader';

type Props = {
  entity: HistoryEntity;
  closeDialog: () => void;
};

function EntityHistory({ entity, closeDialog }: Props) {
  const [data, setData] = useState<{
    statuses: TExeStatus[];
    createdAt: string;
  } | null>(null);
  const { statuses, createdAt } = data || {};

  const { showError } = useMessageModal();

  useEffect(() => {
    (entity.entity === 'word'
      ? apiGetWord(entity.uid).then(word =>
          setData({
            createdAt: word.createdAt,
            statuses: getExercisesStatuses(word).map(st => ({
              label: EXERCISE_LABELS[st.exercise],
              lastProbeAt: st.lastProbeAt,
              nextProbeAt: st.nextProbeAt,
              reseted: st.reseted,
              tryouts: st.tryouts,
            })),
          })
        )
      : apiGetQuestion(entity.uid).then(question =>
          setData({
            createdAt: question.createdAt,
            statuses: [
              {
                label: 'Question',
                lastProbeAt: question.lastProbeAt,
                nextProbeAt: question.nextProbeAt,
                reseted: question.reseted,
                tryouts: question.tryouts,
              },
            ],
          })
        )
    ).catch(showError);
  }, [entity]);

  if (!statuses || !createdAt) {
    return (
      <div className={css.root}>
        <Preloader className={css.preloader} />
      </div>
    );
  }

  return (
    <div className={css.root}>
      <div className={css.created}>
        Created at: {<LongDateTime date={createdAt} />}
      </div>
      <div className={css.body}>
        {statuses.map((status, idx) => (
          <Fragment key={status.label}>
            <Exercise status={status} />
            {idx === 0 && <Graph />}
          </Fragment>
        ))}
        {statuses.length === 1 && <div className={css.exercise} />}
      </div>
      <div className={css.actions}>
        <Button onClick={() => null} disabled children="Reset" />
        <Button onClick={() => null} disabled children="/2" />
        <Button onClick={closeDialog} kkb="Escape" kkbCheckInControls={false}>
          Close
        </Button>
      </div>
    </div>
  );
}

function Exercise({ status }: { status: TExeStatus }) {
  return (
    <div className={css.exercise}>
      <h3 className={css.exeName}>{status.label}</h3>
      <div className={css.attemptsList}>
        <Attempts mode="reseted" value={status.reseted} />
        <Attempts mode="passed" value={status.tryouts - status.reseted} />
      </div>
      <TimeSlot label="Last probe" date={status.lastProbeAt} />
      <TimeSlot label="Next probe" date={status.nextProbeAt} />
    </div>
  );
}

function Attempts({
  mode,
  value,
}: {
  mode: 'reseted' | 'passed';
  value: number;
}) {
  return (
    <div>
      <span
        className={
          mode === 'passed' || value === 0 ? css.positive : css.negative
        }
      >
        {value}
      </span>{' '}
      times {mode}
    </div>
  );
}

const onlyDateF = new Intl.DateTimeFormat(navigator.language, {
  dateStyle: 'short',
});
const shortDateTimeF = new Intl.DateTimeFormat(navigator.language, {
  dateStyle: 'short',
  timeStyle: 'short',
});

function TimeSlot({ label, date }: { label: string; date: string }) {
  return (
    <div className={css.timeSlot} title={shortDateTimeF.format(new Date(date))}>
      <h3 className={css.timeSlotLabel}>{label}</h3>
      <div className={css.timeSlotDate}>
        {shortDateTimeF.format(new Date(date))}
      </div>
      <div className={css.timeSlotDate}>{getRelativeTime(date)}</div>
    </div>
  );
}

function LongDateTime({ date }: { date: string }) {
  const big = dayjs().diff(date, 'day') > 1;

  return big
    ? onlyDateF.format(new Date(date)) + ' (' + getRelativeTime(date) + ')'
    : getRelativeTime(date);
}

function Graph() {
  return (
    <div className={css.graph}>
      <div className={css.point} />
      <div className={css.line} />
      <div className={css.point} />
      <div className={css.line} />
      <div className={cx(css.point, css.positive)} />
    </div>
  );
}

export default memo(EntityHistory);

export type HistoryEntity = {
  uid: string;
  entity: 'question' | 'word';
};

type TExeStatus = TExerciseStatus & {
  label: string;
};
